import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon, Pencil2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetSettings } from "src/api/General/settings";
import { useGetUsers } from "src/api/General/users";
import {
  SpaceMembershipData,
  useAddUserToSpace,
  useDeleteSpace,
  useGetSpace,
  useGetSpaceMemberships,
  useRemoveUserFromSpace,
  useUpdateSpaceMembership,
} from "src/api/Spaces/spaces";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import InputHeading from "src/components/ui/Headings/InputHeading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { SpaceNameAndSymbol } from "src/pages/spaces/spaces";

type Props = {};

export default function SpaceMembersSettings({}: Props) {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const { data: settings } = useGetSettings();
  const [deletingOpen, setDeletingOpen] = useState(false);
  const deleteSpaceMutation = useDeleteSpace();
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  useEffect(() => {
    if (deleteSpaceMutation.isSuccess) {
      setDeletingOpen(false);
    }
  }, [deleteSpaceMutation.isSuccess]);
  const [open, setOpen] = useState(false);
  const addUserToSpaceMutation = useAddUserToSpace();
  const { data: memberships } = useGetSpaceMemberships(String(spaceId));
  const { users } = useGetUsers();
  const addOptions = useMemo(() => {
    if (!users || !memberships) return [];
    return users.filter(
      (user) =>
        !memberships?.some((m) => m.user._id.toString() === user._id.toString())
    );
  }, [memberships, users]);

  return (
    <div>
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings/spaces/${spaceId}`}>
            {space && (
              <SpaceNameAndSymbol name={space.name} color={space.color} />
            )}
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Members"}</div>
        </div>
      </div>
      <div className="mx-auto max-w-2xl pt-20">
        <div className="space-y-1 border-b border-gray-200/70 pb-6">
          <div className="text-xl font-medium">Members</div>
          <div className="text-sm font-light text-gray-600">
            View and edit members of your space
          </div>
        </div>
        <div className="space-y-12 pt-6">
          <div className="space-y-6">
            <div className="flex justify-end">
              <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="secondary"
                    role="combobox"
                    aria-expanded={open}
                    disabled={!memberships || !users}
                    className={clsx(
                      "group relative truncate font-normal"
                      // selectedProject?.stages.length && "rounded-r-none border-r-0"
                    )}
                  >
                    Add Member
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  animateOut={false}
                  className="pointer-events-auto w-60 p-0"
                  align="end"
                >
                  {!!addOptions.length ? (
                    <>
                      <Command>
                        <CommandInput
                          placeholder="Search user..."
                          className="h-9"
                        />
                        <CommandList>
                          <CommandEmpty>No user found.</CommandEmpty>
                          <CommandGroup>
                            {addOptions?.map((user) => (
                              <CommandItem
                                key={user._id.toString()}
                                value={user._id.toString()}
                                onSelect={() => {
                                  addUserToSpaceMutation.mutate({
                                    spaceId: String(spaceId),
                                    userId: user._id.toString(),
                                    role: "member",
                                  });
                                  setOpen(false);
                                }}
                                className="gap-2"
                              >
                                <span className="truncate">{user.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </>
                  ) : (
                    <div className="px-4 py-2.5 text-sm">
                      <div className="select-none">No users left to add</div>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            {/* <div className="space-y-0.5">
              <div className="font-medium text-gray-800">All Members</div>
              <div className="text-sm font-light text-gray-600"></div>
            </div> */}
            <div className="rounded-md border border-gray-200 p-8 pt-7 pb-4 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 pb-3 font-normal text-gray-500">
                <div className="flex-1 text-sm leading-none">Name</div>
                <div className="w-60 text-sm leading-none">Email</div>
                <div className="w-28 text-sm leading-none">Role</div>
                <div className="w-10 text-sm leading-none" />
              </div>
              {memberships?.map((membership) => (
                <UserRow
                  key={membership._id.toString()}
                  membership={membership}
                />
              ))}
            </div>

            {/* <div className="p-6 rounded shadow-sm bg-gray-50">
              <div className="border-b flex items-center h-7">
                <div className="flex-1 text-gray-500">Name</div>
                <div className="w-72 text-gray-500">Email</div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

function UserRow({ membership }: { membership: SpaceMembershipData }) {
  const { spaceId } = useParams();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const updateMembershipMutation = useUpdateSpaceMembership();
  const removeMembershipMutation = useRemoveUserFromSpace();
  const { data: space } = useGetSpace(String(spaceId));

  useEffect(() => {
    if (removeMembershipMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [removeMembershipMutation.isSuccess]);
  return (
    <div className="flex h-10 items-center gap-2 border-b border-gray-100 last:border-0">
      <div className="flex-1 truncate text-sm font-medium leading-none">
        {membership.user.name}
      </div>
      <div className="w-60 truncate text-sm leading-none">
        {membership.user.email}
      </div>
      <div className="w-28 text-sm leading-none">
        {membership.role === "admin" ? "Admin" : "Member"}
      </div>
      <div className="flex w-10 justify-end">
        <DropdownMenu
          open={optionsOpen}
          onOpenChange={(isOpen) => {
            setOptionsOpen(isOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={"ghost"}
              className={clsx(
                "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
              )}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44" align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                className="focus:bg-red-50 focus:text-red-600"
                onClick={(e) => {
                  setIsDeleting(true);
                  e.stopPropagation();
                }}
              >
                <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Remove</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Membership</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-1.5">
                <InputHeading heading="Member" />
                <div className="text-sm font-medium text-gray-800">
                  {membership.user.name}
                </div>
              </div>
              <div className="space-y-1.5">
                <InputHeading heading="Space" />
                {space && (
                  <SpaceNameAndSymbol name={space.name} color={space.color} />
                )}
              </div>
              <div className="space-y-1">
                <InputHeading heading="Role" />
                <Select
                  value={membership.role}
                  onValueChange={(value) => {
                    if (!spaceId) return;
                    updateMembershipMutation.mutate({
                      userId: membership.user._id,
                      spaceId: spaceId,
                      role: value as "admin" | "member",
                    });
                  }}
                >
                  <SelectTrigger className="w-40">
                    {membership.role === "admin" ? "Admin" : "Member"}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Removal</DialogTitle>
            </DialogHeader>

            <div className="my-4">
              <div className="text-sm">
                <span className="font-light text-gray-600">{`Are you sure you want to remove `}</span>
                <span className="font-medium">{`${membership.user.name}`}</span>
                <span className="font-light text-gray-600">{` from this space?`}</span>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                onClick={() => {
                  setIsDeleting(false);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                disabled={removeMembershipMutation.isLoading}
                onClick={() => {
                  removeMembershipMutation.mutate({
                    spaceId: String(spaceId),
                    userId: String(membership.user._id),
                  });
                }}
                variant={"destructive"}
              >
                {removeMembershipMutation.isLoading ? "Removing.." : "Remove"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
